import React, { useState, useEffect, useRef } from 'react';
import {
  Edit, Search, Filter, X,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  ReceiptText, KeyRound, ChevronDown, ChevronUp,
  ToggleLeft, ToggleRight,
} from 'lucide-react';
import config from '../../config';
import { EmployeeListProps } from '../common/types';
import { PaginationMeta } from '../../api/doctor';
import { EmployeeServerFilters, assignRoleToEmployee, toggleEmployeeStatus } from '../../api/employee';
import { getAllDesignations, getAllHeadquarters, getAllZones } from '../../api/master';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../../utils/toast';
import ChangePasswordModal from '../common/ChangePasswordModal';

interface EmployeeListExtendedProps extends EmployeeListProps {
  pagination?: PaginationMeta;
  onPageChange?: (page: number) => void;
  serverFilters?: EmployeeServerFilters;
  onServerFilterChange?: (filters: EmployeeServerFilters) => void;
  onStatusToggled?: () => void;
}

// ---------------------------------------------------------------------------
// HQ cell: shows first 2 badges; expand button reveals the rest inline
// ---------------------------------------------------------------------------
const HqCell: React.FC<{ hqs: { id: number; name: string }[] }> = ({ hqs }) => {
  const [expanded, setExpanded] = useState(false);

  if (!hqs || hqs.length === 0) return <span className="text-gray-400">-</span>;

  const visible = expanded ? hqs : hqs.slice(0, 2);
  const hidden  = hqs.length - 2;

  return (
    <div className="flex flex-wrap gap-1 items-center min-w-[120px]">
      {visible.map(hq => (
        <span
          key={hq.id}
          className="inline-block px-2 py-0.5 text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full whitespace-nowrap"
        >
          {hq.name}
        </span>
      ))}
      {!expanded && hidden > 0 && (
        <button
          onClick={() => setExpanded(true)}
          className="inline-flex items-center gap-0.5 px-2 py-0.5 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full border border-blue-200 whitespace-nowrap transition-colors"
        >
          +{hidden} more <ChevronDown className="w-3 h-3" />
        </button>
      )}
      {expanded && hqs.length > 2 && (
        <button
          onClick={() => setExpanded(false)}
          className="inline-flex items-center gap-0.5 px-2 py-0.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full border border-gray-200 whitespace-nowrap transition-colors"
        >
          less <ChevronUp className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const EmployeeList: React.FC<EmployeeListExtendedProps> = ({
  employees, isLoading, onEdit, onViewExpenses,
  pagination, onPageChange,
  serverFilters = {}, onServerFilterChange,
  onStatusToggled,
}) => {
  const [searchTerm, setSearchTerm]           = useState(serverFilters.search ?? '');
  const [showFilters, setShowFilters]         = useState(false);
  const [showAssignModel, setShowAssignModal] = useState(false);
  const [allDesignations, setAllDesignations] = useState<{ id: number; name: string }[]>([]);
  const [allHeadQuarters, setAllHeadQuarters] = useState<{ id: number; name: string }[]>([]);
  const [allZones, setAllZones]               = useState<{ id: number; name: string }[]>([]);
  const [allWorkTypes, setAllWorkTypes]       = useState<string[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId]     = useState<number>(0);
  const [selectedDesignationId, setSelectedDesignationId] = useState<number>(0);
  const [assignLoading, setAssignLoading]     = useState(false);
  const [changePasswordTarget, setChangePasswordTarget] =
    useState<{ userId: number; name: string } | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const { user } = useAuth();
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track whether we've ever had data so we can use an overlay instead of
  // replacing the whole table on subsequent loads.
  const hasFetchedOnce = useRef(false);
  if (!isLoading) hasFetchedOnce.current = true;

  useEffect(() => {
    if (user?.tenant_id) fetchDropdowns();
  }, [user]);

  // Sync local search box when parent clears filters
  useEffect(() => {
    setSearchTerm(serverFilters.search ?? '');
  }, [serverFilters.search]);

  // Accumulate distinct work_type values across pages for the dropdown
  useEffect(() => {
    if (employees.length > 0) {
      setAllWorkTypes(prev => Array.from(new Set([
        ...prev,
        ...employees.map(e => e.work_type).filter((v): v is string => !!v),
      ])));
    }
  }, [employees]);

  const fetchDropdowns = async () => {
    try {
      const [desigs, hqs, zones] = await Promise.all([
        getAllDesignations(user.tenant_id),
        getAllHeadquarters(user.tenant_id),
        getAllZones(user.tenant_id),
      ]);
      setAllDesignations(desigs);
      setAllHeadQuarters(hqs);
      setAllZones(zones);
    } catch (err) {
      console.error('Failed to load filter dropdowns', err);
    }
  };

  const handleRoleAssign = async () => {
    if (!selectedEmployeeId || !selectedDesignationId) return;
    try {
      setAssignLoading(true);
      await assignRoleToEmployee(user.tenant_id, selectedDesignationId, selectedEmployeeId);
      setShowAssignModal(false);
      setSelectedEmployeeId(0);
      setSelectedDesignationId(0);
    } catch (error) {
      console.error('Role assign failed', error);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleToggleStatus = async (employee: any) => {
    const action = employee.is_active ? 'deactivate' : 'activate';
    const confirmed = await toast.confirm(
      `${employee.user.name} will be ${action}d.`,
      `${action.charAt(0).toUpperCase() + action.slice(1)} employee?`,
      action.charAt(0).toUpperCase() + action.slice(1),
    );
    if (!confirmed) return;
    setTogglingId(employee.id);
    try {
      await toggleEmployeeStatus(employee.id);
      toast.success(`Employee ${action}d successfully.`);
      onStatusToggled?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? `Failed to ${action} employee.`);
    } finally {
      setTogglingId(null);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      onServerFilterChange?.({ ...serverFilters, search: value || undefined });
    }, 400);
  };

  const handleFilterChange = (key: keyof EmployeeServerFilters, value: string | number | undefined) => {
    onServerFilterChange?.({ ...serverFilters, [key]: value || undefined });
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    onServerFilterChange?.({});
  };

  const hasActiveFilters = !!(
    serverFilters.search ||
    serverFilters.work_type ||
    serverFilters.zone_id ||
    serverFilters.head_quarter_id ||
    serverFilters.designation_id
  );

  // Full-page spinner only on the very first load when there's nothing to show yet
  if (isLoading && !hasFetchedOnce.current) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          <p className="text-gray-500 mt-2">Loading employees...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

      {/* ------------------------------------------------------------------ */}
      {/* Search + Filters bar                                                 */}
      {/* ------------------------------------------------------------------ */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name, email, code, contact..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="block w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchTerm && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 text-sm border rounded-lg transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4 mr-1.5" />
            Filters
            {hasActiveFilters && (
              <span className="ml-2 bg-blue-600 text-white text-xs font-medium w-4 h-4 flex items-center justify-center rounded-full">
                {[serverFilters.work_type, serverFilters.zone_id, serverFilters.head_quarter_id, serverFilters.designation_id].filter(Boolean).length}
              </span>
            )}
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="flex items-center px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Clear all
            </button>
          )}
        </div>

        {/* Filter dropdowns */}
        {showFilters && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Work Type</label>
              <select
                value={serverFilters.work_type ?? ''}
                onChange={(e) => handleFilterChange('work_type', e.target.value || undefined)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Types</option>
                {allWorkTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Designation</label>
              <select
                value={serverFilters.designation_id ?? ''}
                onChange={(e) => handleFilterChange('designation_id', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Designations</option>
                {allDesignations.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Zone</label>
              <select
                value={serverFilters.zone_id ?? ''}
                onChange={(e) => handleFilterChange('zone_id', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Zones</option>
                {allZones.map(z => (
                  <option key={z.id} value={z.id}>{z.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Head Quarter</label>
              <select
                value={serverFilters.head_quarter_id ?? ''}
                onChange={(e) => handleFilterChange('head_quarter_id', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All HQ</option>
                {allHeadQuarters.map(hq => (
                  <option key={hq.id} value={hq.id}>{hq.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Results summary */}
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
          {isLoading && (
            <span className="inline-block w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          )}
          {pagination ? (
            pagination.total === 0 ? (
              <span>No employees found{hasActiveFilters && ' for current filters'}</span>
            ) : (
              <span>
                Showing{' '}
                <span className="font-medium">
                  {((pagination.current_page - 1) * pagination.per_page) + 1}–{Math.min(pagination.current_page * pagination.per_page, pagination.total)}
                </span>
                {' '}of{' '}
                <span className="font-medium">{pagination.total}</span> employees
                {hasActiveFilters && <span className="ml-1 text-blue-600">(filtered)</span>}
              </span>
            )
          ) : (
            <span>{employees.length} employees</span>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Table — stays visible during re-fetches, just dimmed                */}
      {/* ------------------------------------------------------------------ */}
      <div className={`overflow-x-auto transition-opacity duration-150 ${isLoading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Work Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assign To</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zone</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Head Quarters</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DOB</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DOJ</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {employees.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-6 py-10 text-center text-gray-400 text-sm">
                  {hasActiveFilters
                    ? 'No employees match the current filters.'
                    : 'No employees found. Add your first employee to get started.'}
                </td>
              </tr>
            ) : employees.map((employee) => (
              <tr key={employee.id} className="hover:bg-gray-50 transition-colors">
                {/* Actions */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => onEdit(employee)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Edit employee"
                    >
                      <Edit className="w-4 h-4" />
                    </button>

                    {/* Delete button commented out — use active/inactive toggle instead */}
                    {/* {onDelete && (
                      <button
                        onClick={() => onDelete(employee.id.toString())}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Delete employee"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )} */}

                    <button
                      onClick={() => handleToggleStatus(employee)}
                      disabled={togglingId === employee.id}
                      className={`p-1.5 rounded transition-colors ${
                        employee.is_active !== 0
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-400 hover:bg-gray-100'
                      } disabled:opacity-50`}
                      title={employee.is_active !== 0 ? 'Deactivate employee' : 'Activate employee'}
                    >
                      {employee.is_active !== 0
                        ? <ToggleRight className="w-5 h-5" />
                        : <ToggleLeft className="w-5 h-5" />}
                    </button>

                    {onViewExpenses && (
                      <button
                        onClick={() => onViewExpenses(employee.id)}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                        title="View expenses"
                      >
                        <ReceiptText className="w-4 h-4" />
                      </button>
                    )}

                    <button
                      onClick={() => setChangePasswordTarget({ userId: employee.user.id, name: employee.user.name })}
                      className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                      title="Change password"
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>

                    {!employee.role && (
                      <button
                        onClick={() => {
                          setSelectedEmployeeId(employee.user.id!);
                          setSelectedDesignationId(employee.designation_id!);
                          setShowAssignModal(true);
                        }}
                        className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                      >
                        Assign Role
                      </button>
                    )}
                  </div>
                </td>

                {/* Employee (merged: avatar + name + code) */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <img
                      src={employee.photo ? `${config.apiBaseUrl}/user-profile-image/${employee.photo}` : '/placeholder-user.png'}
                      alt="Profile"
                      className="h-9 w-9 rounded-full object-cover border border-gray-200 flex-shrink-0"
                      onError={(e) => (e.currentTarget.src = '/placeholder-user.png')}
                    />
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${employee.is_active === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
                        {employee.user.name}
                      </p>
                      <p className="text-xs font-mono text-gray-500">{employee.code || '-'}</p>
                    </div>
                  </div>
                </td>

                {/* Designation */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                  {employee.designation?.name ?? (employee.designation_id ? `#${employee.designation_id}` : '-')}
                </td>

                {/* Work Type */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                    employee.work_type === 'ONFIELD'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {employee.work_type || '-'}
                  </span>
                </td>

                {/* Assign To */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                  {employee.assigned_to?.name || '-'}
                </td>

                {/* Email */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                  {employee.user.email}
                </td>

                {/* Contact */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                  {employee.mobile || employee.alternate_mobile || '-'}
                </td>

                {/* Zone */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                  {employee.zone?.name ?? (employee.zone_id ? `Zone #${employee.zone_id}` : '-')}
                </td>

                {/* Head Quarters */}
                <td className="px-4 py-3">
                  <HqCell hqs={employee.head_quarters ?? []} />
                </td>

                {/* DOB */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                  {employee.dob || '-'}
                </td>

                {/* DOJ */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                  {employee.doj || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Pagination                                                           */}
      {/* ------------------------------------------------------------------ */}
      {pagination && pagination.last_page > 1 && onPageChange && (
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-gray-600">
            Page <span className="font-medium">{pagination.current_page}</span> of{' '}
            <span className="font-medium">{pagination.last_page}</span>
          </p>

          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(1)}
              disabled={pagination.current_page === 1}
              className="p-1.5 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              title="First page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => onPageChange(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className="p-1.5 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
              .filter(p => p === 1 || p === pagination.last_page || Math.abs(p - pagination.current_page) <= 1)
              .map((p, idx, arr) => (
                <React.Fragment key={p}>
                  {idx > 0 && arr[idx - 1] !== p - 1 && (
                    <span className="px-1 text-gray-400 text-sm">…</span>
                  )}
                  <button
                    onClick={() => onPageChange(p)}
                    className={`min-w-[2rem] px-2 py-1.5 text-sm rounded border transition-colors ${
                      p === pagination.current_page
                        ? 'bg-blue-600 text-white border-blue-600 font-medium'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {p}
                  </button>
                </React.Fragment>
              ))}

            <button
              onClick={() => onPageChange(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
              className="p-1.5 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => onPageChange(pagination.last_page)}
              disabled={pagination.current_page === pagination.last_page}
              className="p-1.5 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              title="Last page"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Assign Role Modal                                                    */}
      {/* ------------------------------------------------------------------ */}
      {showAssignModel && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Assign Designation</h3>
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
              <select
                value={selectedDesignationId}
                onChange={(e) => setSelectedDesignationId(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select designation</option>
                {allDesignations.map((d: any) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowAssignModal(false); setSelectedEmployeeId(0); setSelectedDesignationId(0); }}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                disabled={!selectedDesignationId || assignLoading}
                onClick={handleRoleAssign}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700"
              >
                {assignLoading ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ChangePasswordModal
        isOpen={!!changePasswordTarget}
        onClose={() => setChangePasswordTarget(null)}
        userId={changePasswordTarget?.userId ?? 0}
        userName={changePasswordTarget?.name ?? ''}
        isSelf={false}
      />
    </div>
  );
};

export default EmployeeList;
